import { useContext, useState, useEffect, useRef } from "react";
import { CartContext } from "../Contexts/CartContext";
import { AvailabilityContext } from "../Contexts/AvailabilityContext";
import { ORDER_API_URL, getPaypalSdkUrl, buildPaypalIpnUrl } from "../Data/apiEndpoints";
import { v4 as uuidv4 } from "uuid";
import useCheckoutForm from "./useCheckoutForm";
import useShipping from "./useShipping";
import { checkout } from "../Data/siteContent";

export default function useCheckoutLogic() {
  const { cartItems, clearCart } = useContext(CartContext);
  const { sandbox } = useContext(AvailabilityContext);
  const paypalRef = useRef();
  const [sdkReady, setSdkReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");

  const { formData, fieldErrors, handleInputChange, validateFields, isFormValid } = useCheckoutForm();
  const { shippingCost, total, countries } = useShipping(formData.country, cartItems);

  useEffect(() => {
    const addPayPalScript = () => {
      const script = document.createElement("script");
      script.src = getPaypalSdkUrl(sandbox);
      script.type = "text/javascript";
      script.async = true;
      script.onload = () => setSdkReady(true);
      document.body.appendChild(script);
    };

    if (!window.paypal) {
      addPayPalScript();
    } else {
      setSdkReady(true);
    }
  }, [sandbox]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const paymentSuccess = queryParams.get("success");

    if (paymentSuccess === "true") {
      handlePaymentSuccess();
    } else if (paymentSuccess === "false") {
      setCheckoutError(checkout.paymentFailed);
    }
  }, []);

  const handlePaymentSuccess = data => {
    if (!data || typeof data !== "object") {
      return;
    }

    const { transactionId, email } = data;

    if (!transactionId || !email) {
      setCheckoutError(checkout.missingData);
      return;
    }

    setCheckoutError("");
  };

  const closeCheckoutError = () => {
    setCheckoutError("");
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (sdkReady && paypalRef.current) {
      window.paypal.Buttons().render(paypalRef.current);
    }
  };

  const handlePayment = async () => {
    if (!validateFields()) {
      return;
    }
    setIsLoading(true);
    const orderId = uuidv4();
    const purchaseData = {
      orderId,
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      address: formData.address,
      apartment: formData.apartment,
      city: formData.city,
      zipCode: formData.zipCode,
      country: formData.country,
      phone: formData.phone,
      cartItems: cartItems.map(item => ({
        name: item.name,
        price: parseFloat(item.price.replace(/[^0-9.]/g, "")),
        qty: item.quantity || 1,
        image: item.image,
        secondTitle: item.secondTitle,
      })),
      total: total.toFixed(2),
      shipping: shippingCost.toFixed(2),
    };
    try {
      const response = await fetch(ORDER_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(purchaseData),
      });

      if (!response.ok) {
        throw new Error("Error during call to ORDER_API_URL");
      }
      const result = await response.json();
      sessionStorage.setItem(
        "lastOrder",
        JSON.stringify({
          commercialId: result.commercialId,
          timestamp: result.timestamp,
          items: cartItems.map(item => ({
            name: item.name,
            secondTitle: item.secondTitle,
            image: item.image,
            price: item.price,
            quantity: item.quantity || 1,
          })),
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          address: formData.address,
          apartment: formData.apartment,
          city: formData.city,
          zipCode: formData.zipCode,
          country: formData.country,
          phone: formData.phone,
          shipping: shippingCost,
          total,
        })
      );
      clearCart();
      window.location.href = buildPaypalIpnUrl(total, orderId, sandbox);
    } catch (error) {
      setCheckoutError(checkout.errorDuringPayment);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);

    const transactionId = queryParams.get("tx") || null;
    const status = queryParams.get("st") || null;
    const amount = queryParams.get("amt") || null;
    const currency = queryParams.get("cc") || null;
    const email = queryParams.get("payer_email") || null;
    const firstName = queryParams.get("first_name") || null;
    const lastName = queryParams.get("last_name") || null;

    if (status === "Completed" && transactionId && email) {
      handlePaymentSuccess({
        transactionId,
        amount,
        currency,
        email,
        firstName,
        lastName,
      });
    }
  }, []);

  return {
    cartItems,
    paypalRef,
    sdkReady,
    isLoading,
    checkoutError,
    formData,
    fieldErrors,
    handleInputChange,
    isFormValid,
    shippingCost,
    total,
    countries,
    handleSubmit,
    handlePayment,
    closeCheckoutError,
  };
}
